/**
 * Custom start-case implementation that only splits on whitespace
 * Preserves hyphens and apostrophes in names like "Jean-Pierre" or "O'Brien"
 */
const customStartCase = (str: string): string => {
    return str
        .split(/\s+/)
        .map((word) => {
            if (!word) return word;
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(" ");
};

export const uppercaseFirstCharacter = (str: string) => {
    return str?.slice(0, 1).toUpperCase() + str?.slice(1);
};

/**
 * Detects if a string contains Asian characters (Japanese, Korean, Chinese)
 * Used to determine if name capitalization should be applied
 */
const hasAsianCharacters = (str: string): boolean => {
    if (!str) return false;

    // Unicode ranges:
    // Hiragana: \u3040-\u309F
    // Katakana: \u30A0-\u30FF
    // CJK Unified Ideographs (Kanji/Chinese): \u4E00-\u9FFF (extended range)
    // Hangul (Korean): \uAC00-\uD7AF
    const asianCharRegex = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uAC00-\uD7AF]/;
    return asianCharRegex.test(str);
};

/**
 * Formats a name with proper capitalization, handling special cases
 * Only applies to Latin alphabet text - preserves Asian characters as-is
 * Preserves hyphens and apostrophes in compound names
 *
 * Examples:
 * - "john doe" → "John Doe"
 * - "jean-pierre" → "Jean-Pierre"
 * - "mcdonald" → "McDonald"
 * - "o'brien" → "O'Brien"
 * - "田中" → "田中" (preserved)
 * - "김민수" → "김민수" (preserved)
 */
export const formatNameForLocale = (name: string): string => {
    if (!name) return "";

    const MAX_NAME_LENGTH = 100;
    if (name.length > MAX_NAME_LENGTH) return name;

    if (hasAsianCharacters(name)) {
        return name;
    }

    // Use custom start-case that only splits on whitespace (preserves hyphens and apostrophes)
    let formatted = customStartCase(name);

    // Handle Scottish/Irish prefixes
    formatted = formatted.replace(/\bMc([a-z])/gi, (_match, letter) => "Mc" + letter.toUpperCase());
    formatted = formatted.replace(
        /\bMac([a-z])/gi,
        (_match, letter) => "Mac" + letter.toUpperCase()
    );

    // Handle O' prefix
    formatted = formatted.replace(/\bO'([a-z])/gi, (_match, letter) => "O'" + letter.toUpperCase());

    // Handle hyphenated names (e.g., "jean-pierre" → "Jean-Pierre")
    formatted = formatted.replace(/-([a-z])/g, (_match, letter) => "-" + letter.toUpperCase());

    return formatted;
};

// number
export const isNumber = (value: number | string) => typeof value === "number" && isFinite(value);

export const isIOSDevice = () => {
    if (typeof window !== "undefined" && typeof navigator !== "undefined") {
        return /iPad|iPhone|iPod/.test(navigator?.userAgent ?? "");
    }
    return false;
};

// styles
export const calcVwWidth = (ratio: number) => {
    if (!ratio || ratio <= 0) {
        return "100vw";
    }
    return `calc(100vw/${ratio})`;
};
